<template>
	<nav class="tabs-v2">
		<!-- Видимые вкладки -->
		<button
			v-for="tab in visibleTabs"
			:key="tab.id"
			class="tab"
			:class="{ active: tab.id === resolvedActiveTab }"
      :style="tabStyle(tab)"
			@click="goToTab(tab)"
		>
			<slot :tab="tab">
				<span class="tab-name">{{ tab.displayName ?? tab.name }}</span>
			</slot>
		</button>

		<!-- Кнопка «ещё» + выпадашка со скрытыми вкладками -->
		<template v-if="hiddenTabs.length > 0">
			<button
				:id="moreButtonId"
				class="tab more"
				:class="{
					active: isHiddenTabActive,
					open:   isPopupVisible,
				}"
        :style="tabStyle(activeHiddenTab)"
				@mouseenter="openPopup"
				@mouseleave="closePopup"
			>
				<VIcon class="more-arrow" material="keyboard_arrow_down" solid />

				<span v-if="isHiddenTabActive === false" class="more-icon">
					<VIcon material="dehaze" solid />
				</span>
				<span v-else class="tab-name">
					{{ activeHiddenTab.displayName ?? activeHiddenTab.name }}
				</span>
			</button>

			<VPopup
				ref="popup"
				class="tabs-v2-popup"
				:target="moreButtonTarget"
				:visible="isPopupVisible"
				:offset="-2"
				align="left"
        @mouseenter="openPopup"
				@mouseleave="closePopup"
				@close="closePopup"
			>
				<button
					v-for="tab in hiddenTabs"
					:key="tab.id"
					class="popup-tab"
					:class="{ active: tab.id === resolvedActiveTab }"
          :style="tabStyle(tab)"
					@click="goToTab(tab)"
				>
					<slot :tab="tab">
						<span class="tab-name">{{ tab.displayName ?? tab.name }}</span>
					</slot>
				</button>
			</VPopup>
		</template>
	</nav>
</template>

<style lang="scss" src="./VTabsV2.scss" />

<script lang="ts">
import Core       from '@/core/Core';
import TabElement from './TabElement';

export default defineComponent({
	name: 'VTabsV2',

	props: {
		/** Список вкладок */
		tabs: {
			type:    Array as PropType<TabElement[]>,
			default: () => [],
		},

		/**
		 * Активная вкладка, объект TabElement (state-mode).
		 *
		 * Режимы взаимоисключающие:
		 * - **state-mode** — передан `activeTab`, родитель держит активную вкладку
		 *   сам, клик по вкладке эмитит `update`;
		 * - **router-mode** — передан `baseUrl`, активная вкладка вычисляется
		 *   из `$route` (см. resolvedActiveTab), клик пушит в роутер.
		 *
		 * Переданы оба — Core.warn, побеждает `baseUrl`. Для сложных роутов,
		 * где сегмент пути не совпадает с id вкладки, использовать state-mode
		 * с ручным push (пример — TendersPage, таб в хэше).
		 *
		 * В router-mode проп не передавать, но слушать `@update` можно:
		 * компонент эмитит вкладку, вычисленную из роута, — страницам
		 * не нужно парсить $route самим (пример — OrdersPage).
		 */
		activeTab: {
			type:    Object as PropType<TabElement>,
			default: null,
		},

		/**
		 * Базовый путь. Если задан - компонент работает в **router-mode**:
		 * клик по вкладке делает `$router.push('${baseUrl}/${tab.id}')`,
		 * а активный id родитель читает из `$route.params`.
		 *
		 * Если НЕ задан - компонент работает в **state-mode**:
		 * клик по вкладке эмитит `update:activeId`, ничего в роутер не пушит.
		 * Используется для табов внутри диалогов / страниц без URL-навигации.
		 */
		baseUrl: {
			type:    String,
			default: null,
		},
	},

	emits: ['update'],

	data() {
		return {
			isPopupVisible: false,
		};
	},

	created() {
		if (this.baseUrl !== null && Core.utils.isNullOrUndefined(this.activeTab) === false) {
			Core.warn('[VTabsV2] Переданы одновременно base-url и active-tab — режимы взаимоисключающие, побеждает base-url.');
		}
	},

	computed: {
		/**
		 * Фактический id активной вкладки.
		 *
		 * router-mode (задан baseUrl): вычисляется из роута — берётся сегмент
		 * пути сразу после baseUrl. Дефолтный таб для голого baseUrl — зона
		 * ответственности роутера (redirect на дефолтный сегмент), компонент
		 * не подстраховывает.
		 *
		 * state-mode: id из пропа activeTab, родитель — источник правды.
		 *
		 * Матчим именно следующий сегмент, а не endsWith/includes: endsWith
		 * ломается на хвостах роута (`/flow/orders/in-work/123`), includes даёт
		 * ложные срабатывания.
		 */
		resolvedActiveTab(): string | null {
			if (this.baseUrl === null || this.baseUrl.length === 0) {
				return this.activeTab?.id ?? null;
			}

			const path = this.$route.path;

			if (path.startsWith(this.baseUrl) === false) {
				return null;
			}

			// Остаток пути после baseUrl: '/seller' или '/in-work/123' или ''
			const rest = path.slice(this.baseUrl.length);
			// Первый непустой сегмент остатка
			const segment = rest.split('/').filter(part => part.length > 0)[0];

			return segment ?? null;
		},

		visibleTabs(): TabElement[] {
			return this.tabs.filter(tab => tab.hidden !== true);
		},

		hiddenTabs(): TabElement[] {
			return this.tabs.filter(tab => tab.hidden === true);
		},

		/** Активная вкладка, если она среди скрытых, иначе null */
		activeHiddenTab(): TabElement | null {
			return this.hiddenTabs.find(tab => tab.id === this.resolvedActiveTab) ?? null;
		},

		isHiddenTabActive(): boolean {
			return this.activeHiddenTab !== null;
		},

		moreButtonId(): string {
			return `tabs-more-${this.$.uid}`;
		},

		moreButtonTarget(): string {
			return `#${this.moreButtonId}`;
		},
	},

	watch: {
		/**
		 * router-mode: сообщаем родителю вкладку (объект TabElement),

		 * вычисленную из роута, —
		 * страница слушает @update вместо собственного парсинга
		 * $route (тайное знание об именах параметров и структуре пути).
		 * Слушатель — @update.
		 * immediate — чтобы родитель получил начальное значение до своего mounted.
		 *
		 * В state-mode не эмитим: resolvedActiveTab там повторяет проп родителя,
		 * эмит был бы эхом. Эмит state-mode живёт в goToTab.
		 */
		resolvedActiveTab: {
			handler(value: string | null) {
				if (this.baseUrl !== null && this.baseUrl.length > 0) {
					const tab = this.tabs.find(tab => tab.id === value) ?? null;
					this.$emit('update', tab);
				}
			},
			immediate: true,
		},
	},

	methods: {
		/**
		 * Клик по вкладке. Два режима:
		 * - **router-mode** (`baseUrl` задан) — пуш в роутер на `${baseUrl}/${tab.id}`
		 * - **state-mode** (`baseUrl` не задан) — эмит `update` с объектом вкладки
		 */
		goToTab(tab: TabElement): void {
			this.closePopup();

			if (tab.id === this.resolvedActiveTab) return;

			if (this.baseUrl !== null && this.baseUrl.length > 0) {
				this.$router.push({ path: `${this.baseUrl}/${tab.id}` });
			}
			else {
				this.$emit('update', tab);
			}
		},

    /**
     * Цвет вкладки прокидывается в CSS-переменную, из которой стили берут
     * цвет активной вкладки. Цвет не задан — переменная не выставляется,
     * работает дефолт из .tab.
     */
    tabStyle(tab: TabElement | null): Record<string, string> {
      if (tab === null) {
        return {};
      }
      else if (tab.color === null) {
        return {};
      }
      else {
        return { '--active-color': tab.color };
      }
    },

		openPopup(): void {
			this.isPopupVisible = true;
		},

		closePopup(): void {
			this.isPopupVisible = false;
		},
	},
});
</script>
